import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_URL, getAuthHeaders } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { useApiMessages } from "../hooks/useApiMessages";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Calendar, Plus, Trash2, Edit, Globe } from "lucide-react";
import { toast } from "sonner";

export default function Holidays() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const { user } = useAuth();
  const [holidays, setHolidays] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  
  // Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    date: "",
    company_id: "",
    is_recurring: false,
  });

  const canSelectCompany = () => user?.role === "admin" || user?.role === "revendedor";

  useEffect(() => {
    fetchHolidays();
    fetchCompanies();
  }, [selectedYear, selectedCompany]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/companies`, {
        headers: getAuthHeaders(),
      });
      setCompanies(response.data);
    } catch (error) {
      console.error("Error al cargar compañías:", error);
    }
  };

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCompany !== "all") params.append("company_id", selectedCompany);
      params.append("year", selectedYear);

      const response = await axios.get(`${API_URL}/holidays?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      setHolidays(response.data);
    } catch (error) {
      console.error("Error al cargar feriados:", error);
      toast.error("Error al cargar feriados");
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingHoliday(null);
    setFormData({
      name: "",
      date: "",
      company_id: canSelectCompany() ? "" : user.company_id,
      is_recurring: false,
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      name: holiday.name,
      date: holiday.date,
      company_id: holiday.company_id || "",
      is_recurring: holiday.is_recurring,
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.date) {
      toast.error("Nombre y fecha son requeridos");
      return;
    }

    try {
      const payload = {
        name: formData.name,
        date: formData.date,
        company_id: formData.company_id || null,
        is_recurring: formData.is_recurring,
      };

      if (editingHoliday) {
        await axios.put(
          `${API_URL}/holidays/${editingHoliday.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success("Feriado actualizado");
      } else {
        await axios.post(`${API_URL}/holidays`, payload, {
          headers: getAuthHeaders(),
        });
        toast.success("Feriado creado");
      }

      setIsDialogOpen(false);
      fetchHolidays();
    } catch (error) {
      console.error("Error al guardar feriado:", error);
      toast.error(getErrorMessage(error, "api.errors.saveHolidayError"));
    }
  };

  const handleDelete = async (holidayId) => {
    if (!window.confirm(t("pages.holidays.deleteConfirm"))) return;

    try {
      await axios.delete(`${API_URL}/holidays/${holidayId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Feriado eliminado");
      fetchHolidays();
    } catch (error) {
      console.error("Error al eliminar feriado:", error);
      toast.error("Error al eliminar feriado");
    }
  };

  const getCompanyName = (companyId) => {
    if (!companyId) return t("pages.routes.allCompanies");
    const company = companies.find((c) => c.id === companyId);
    return company?.name || "N/A";
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            {t("pages.holidays.title")}
          </h1>
          <p className="text-slate-600 mt-1">
            {t("pages.holidays.subtitle")}
          </p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="h-4 w-4 mr-2" />
          {t("pages.holidays.addHoliday")}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm text-slate-600 mb-1 block">{t("pages.holidays.year")}</Label>
              <Select value={selectedYear.toString()} onValueChange={(val) => setSelectedYear(parseInt(val))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {canSelectCompany() && (
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">
                  {t("pages.companies.name")}
                </Label>
                <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("pages.holidays.all")}</SelectItem>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Holidays Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {t("nav.holidays")} {selectedYear}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">{t("common.loading")}</div>
          ) : holidays.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              {t("pages.holidays.noHolidays")}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("common.date")}</TableHead>
                  <TableHead>{t("common.name")}</TableHead>
                  <TableHead>{t("pages.holidays.scope")}</TableHead>
                  <TableHead>{t("pages.holidays.recurring")}</TableHead>
                  <TableHead className="text-right">{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {holidays.map((holiday) => (
                  <TableRow key={holiday.id}>
                    <TableCell className="font-medium">
                      {new Date(holiday.date + "T00:00:00").toLocaleDateString("es-ES", {
                        weekday: "short",
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </TableCell>
                    <TableCell>{holiday.name}</TableCell>
                    <TableCell>
                      {holiday.company_id ? (
                        <span className="text-sm text-slate-600">
                          {getCompanyName(holiday.company_id)}
                        </span>
                      ) : (
                        <Badge variant="outline" className="gap-1">
                          <Globe className="h-3 w-3" />
                          {t("pages.holidays.global")}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {holiday.is_recurring ? (
                        <Badge variant="secondary">{t("pages.holidays.annual")}</Badge>
                      ) : (
                        <span className="text-sm text-slate-500">{t("common.no")}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(holiday)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(holiday.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingHoliday ? t("pages.holidays.editHoliday") : t("pages.holidays.newHoliday")}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">{t("pages.holidays.holidayName")}</Label>
              <Input
                id="name"
                placeholder={t("pages.holidays.example")}
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </div>

            <div>
              <Label htmlFor="date">{t("common.date")}</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) =>
                  setFormData({ ...formData, date: e.target.value })
                }
              />
            </div>

            {canSelectCompany() && (
              <div>
                <Label htmlFor="company">{t("pages.companies.name")}</Label>
                <Select
                  value={formData.company_id || "global"}
                  onValueChange={(val) =>
                    setFormData({
                      ...formData,
                      company_id: val === "global" ? null : val,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">
                      {t("pages.holidays.allCompanies")}
                    </SelectItem>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Checkbox
                id="recurring"
                checked={formData.is_recurring}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_recurring: checked })
                }
              />
              <Label
                htmlFor="recurring"
                className="text-sm font-normal cursor-pointer"
              >
                {t("pages.holidays.repeatsEveryYear")}
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSave}>
              {editingHoliday ? t("pages.holidays.update") : t("pages.holidays.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
